import { c as _c } from "react/compiler-runtime";
import cx from 'classnames';
import { ComponentProps, createContext, CSSProperties, ImgHTMLAttributes, type Ref, use, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import gfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { isBoolean, omit } from 'lodash';
import AppTypography from '../AppTypography';
import { AnchorHeading } from '../AnchorHeading';
import { getStringTitleSlug } from './helpers';
import './styles.scss';
const IS_EXTERNAL_LINK_PREFIX = 'http';

// Components excluded from receiving the global textColor
const COLOR_EXCLUDED_ELEMENTS = ['link', 'code', 'blockquote', 'image'];

// Interface for component-specific styles
type ComponentStylesType = {
  paragraph?: CSSProperties;
  heading1?: CSSProperties;
  heading2?: CSSProperties;
  heading3?: CSSProperties;
  heading4?: CSSProperties;
  heading5?: CSSProperties;
  link?: CSSProperties;
  list?: CSSProperties;
  listItem?: CSSProperties;
  code?: CSSProperties;
  blockquote?: CSSProperties;
  image?: CSSProperties;
  // Additional property for any unhandled component type
  [key: string]: CSSProperties | undefined;
};
export type MarkdownWrapperProps = ComponentProps<typeof ReactMarkdown> & MarkdownContextType & {
  source: string;
  className?: string;
  style?: CSSProperties;
};

// Create a context to share color and styles across components
type MarkdownContextType = {
  /**
   * Global text color to apply to text elements, excluding code blocks, blockquotes, inline code, and links
   */
  textColor?: string;
  /**
   * Specific styles to apply to different component types
   */
  componentStyles?: ComponentStylesType;
  /**
   * If true, the anchor link will be added to the heading so when the user clicks on it,
   * the anchor id will be added to the url, which can be used to scroll to the heading.
   */
  useAnchorLink?: boolean;
  /**
   * Current hash from URL to highlight matching headings
   */
  currentHash?: string;
};
const MarkdownContext = createContext<MarkdownContextType>({});

// Custom hook to access the markdown context
const useMarkdownContext = () => use(MarkdownContext);

// Helper to apply component-specific styles with textColor
const applyComponentStyles = (componentType: string, textColor?: string, componentStyles?: ComponentStylesType, existingStyle?: CSSProperties): CSSProperties => {
  const styles: CSSProperties = {
    ...existingStyle
  };

  // If this is an excluded element, we should never apply the textColor
  // regardless of its parent
  if (COLOR_EXCLUDED_ELEMENTS.includes(componentType)) {
    // Don't apply textColor
  }
  // Otherwise, apply textColor if provided
  else if (textColor) {
    styles.color = textColor;
  }

  // Apply component-specific styles if provided
  if (componentStyles && componentStyles[componentType]) {
    Object.assign(styles, componentStyles[componentType]);
  }
  return styles;
};
const LinkRenderer: Components['link'] = t0 => {
  const $ = _c(10);
  const {
    href,
    children,
    style,
    ...props
  } = t0;
  const {
    componentStyles
  } = useMarkdownContext();
  const isExternal = href?.startsWith(IS_EXTERNAL_LINK_PREFIX);
  const t1 = componentStyles?.link;
  let t2;
  if ($[0] !== style || $[1] !== t1) {
    t2 = {
      ...style,
      ...t1
    };
    $[0] = style;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const linkStyle = t2;
  const t3 = AppTypography;
  const t4 = "semi-bold";
  const t5 = isExternal ? "_blank" : undefined;
  const t6 = omit(props, "type");
  let t7;
  if ($[3] !== children || $[4] !== href || $[5] !== linkStyle || $[6] !== t3.ExternalLink || $[7] !== t5 || $[8] !== t6) {
    t7 = <t3.ExternalLink href={href} strong={t4} target={t5} style={linkStyle} {...t6}>{children}</t3.ExternalLink>;
    $[3] = children;
    $[4] = href;
    $[5] = linkStyle;
    $[6] = t3.ExternalLink;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
const ATagRenderer: Components['a'] = t0 => {
  const $ = _c(10);
  const {
    href,
    children,
    style,
    ...props
  } = t0;
  const {
    componentStyles
  } = useMarkdownContext();
  const isExternal = href?.startsWith(IS_EXTERNAL_LINK_PREFIX);
  const t1 = componentStyles?.link;
  let t2;
  if ($[0] !== style || $[1] !== t1) {
    t2 = {
      ...style,
      ...t1
    };
    $[0] = style;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const linkStyle = t2;
  const t3 = AppTypography;
  const t4 = "semi-bold";
  const t5 = isExternal ? "_blank" : undefined;
  const t6 = omit(props, "type");
  let t7;
  if ($[3] !== children || $[4] !== href || $[5] !== linkStyle || $[6] !== t3.ExternalLink || $[7] !== t5 || $[8] !== t6) {
    t7 = <t3.ExternalLink href={href} strong={t4} target={t5} style={linkStyle} {...t6}>{children}</t3.ExternalLink>;
    $[3] = children;
    $[4] = href;
    $[5] = linkStyle;
    $[6] = t3.ExternalLink;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
const Title3: Components['h3'] = t0 => {
  const $ = _c(13);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles,
    useAnchorLink: useAnchorLinkCtx,
    currentHash
  } = useMarkdownContext();
  let t1;
  if ($[0] !== children) {
    t1 = getStringTitleSlug(children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const anchor = t1;
  const isHighlighted = currentHash === anchor;
  let t2;
  if ($[2] !== componentStyles || $[3] !== style || $[4] !== textColor) {
    t2 = applyComponentStyles("heading3", textColor, componentStyles, style);
    $[2] = componentStyles;
    $[3] = style;
    $[4] = textColor;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const headingStyle = t2;
  if (useAnchorLinkCtx && anchor) {
    return <AnchorHeading {...omit(props, "ref")} level={2} variant="h3" anchorId={anchor} highlighted={isHighlighted} style={headingStyle} className={props.className}>{children}</AnchorHeading>;
  }
  const t3 = AppTypography;
  const t4 = omit(props, "ref");
  const t5 = 2;
  const t6 = "h3";
  const t7 = cx(props.className, isHighlighted && "markdown-heading-highlighted");
  let t8;
  if ($[6] !== anchor || $[7] !== children || $[8] !== headingStyle || $[9] !== t3.Title || $[10] !== t4 || $[11] !== t7) {
    t8 = <t3.Title {...t4} level={t5} variant={t6} id={anchor} style={headingStyle} className={t7}>{children}</t3.Title>;
    $[6] = anchor;
    $[7] = children;
    $[8] = headingStyle;
    $[9] = t3.Title;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
const Title4: Components['h4'] = t0 => {
  const $ = _c(13);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles,
    useAnchorLink: useAnchorLinkCtx,
    currentHash
  } = useMarkdownContext();
  let t1;
  if ($[0] !== children) {
    t1 = getStringTitleSlug(children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const anchor = t1;
  const isHighlighted = currentHash === anchor;
  let t2;
  if ($[2] !== componentStyles || $[3] !== style || $[4] !== textColor) {
    t2 = applyComponentStyles("heading4", textColor, componentStyles, style);
    $[2] = componentStyles;
    $[3] = style;
    $[4] = textColor;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const headingStyle = t2;
  if (useAnchorLinkCtx && anchor) {
    return <AnchorHeading {...omit(props, "ref")} level={3} variant="h4" anchorId={anchor} highlighted={isHighlighted} style={headingStyle} className={props.className}>{children}</AnchorHeading>;
  }
  const t3 = AppTypography;
  const t4 = omit(props, "ref");
  const t5 = 3;
  const t6 = "h4";
  const t7 = cx(props.className, isHighlighted && "markdown-heading-highlighted");
  let t8;
  if ($[6] !== anchor || $[7] !== children || $[8] !== headingStyle || $[9] !== t3.Title || $[10] !== t4 || $[11] !== t7) {
    t8 = <t3.Title {...t4} level={t5} variant={t6} id={anchor} style={headingStyle} className={t7}>{children}</t3.Title>;
    $[6] = anchor;
    $[7] = children;
    $[8] = headingStyle;
    $[9] = t3.Title;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
const Title5: Components['h5'] = t0 => {
  const $ = _c(13);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles,
    useAnchorLink: useAnchorLinkCtx,
    currentHash
  } = useMarkdownContext();
  let t1;
  if ($[0] !== children) {
    t1 = getStringTitleSlug(children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const anchor = t1;
  const isHighlighted = currentHash === anchor;
  let t2;
  if ($[2] !== componentStyles || $[3] !== style || $[4] !== textColor) {
    t2 = applyComponentStyles("heading5", textColor, componentStyles, style);
    $[2] = componentStyles;
    $[3] = style;
    $[4] = textColor;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const headingStyle = t2;
  if (useAnchorLinkCtx && anchor) {
    return <AnchorHeading {...omit(props, "ref")} level={4} anchorId={anchor} highlighted={isHighlighted} style={headingStyle} className={props.className}>{children}</AnchorHeading>;
  }
  const t3 = AppTypography;
  const t4 = omit(props, "ref");
  const t5 = 4;
  const t6 = cx(props.className, isHighlighted && "markdown-heading-highlighted");
  let t7;
  if ($[6] !== anchor || $[7] !== children || $[8] !== headingStyle || $[9] !== t3.Title || $[10] !== t4 || $[11] !== t6) {
    t7 = <t3.Title {...t4} level={t5} id={anchor} style={headingStyle} className={t6}>{children}</t3.Title>;
    $[6] = anchor;
    $[7] = children;
    $[8] = headingStyle;
    $[9] = t3.Title;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
const Paragraph: Components['p'] = t0 => {
  const $ = _c(9);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles
  } = useMarkdownContext();
  let t1;
  if ($[0] !== componentStyles || $[1] !== style || $[2] !== textColor) {
    t1 = applyComponentStyles("paragraph", textColor, componentStyles, style);
    $[0] = componentStyles;
    $[1] = style;
    $[2] = textColor;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const paragraphStyle = t1;
  const t2 = AppTypography;
  const t3 = omit(props, "ref");
  let t4;
  if ($[4] !== children || $[5] !== paragraphStyle || $[6] !== t2.Paragraph || $[7] !== t3) {
    t4 = <t2.Paragraph {...t3} style={paragraphStyle}>{children}</t2.Paragraph>;
    $[4] = children;
    $[5] = paragraphStyle;
    $[6] = t2.Paragraph;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
const ListRenderer: Components['ul'] = t0 => {
  const $ = _c(8);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles
  } = useMarkdownContext();
  let t1;
  if ($[0] !== componentStyles || $[1] !== style || $[2] !== textColor) {
    t1 = applyComponentStyles("list", textColor, componentStyles, style);
    $[0] = componentStyles;
    $[1] = style;
    $[2] = textColor;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const listStyle = t1;
  const t2 = omit(props, "ref");
  let t3;
  if ($[4] !== children || $[5] !== listStyle || $[6] !== t2) {
    t3 = <ul {...t2} style={listStyle}>{children}</ul>;
    $[4] = children;
    $[5] = listStyle;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const ListItemRenderer: Components['li'] = t0 => {
  const $ = _c(8);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles
  } = useMarkdownContext();
  let t1;
  if ($[0] !== componentStyles || $[1] !== style || $[2] !== textColor) {
    t1 = applyComponentStyles("listItem", textColor, componentStyles, style);
    $[0] = componentStyles;
    $[1] = style;
    $[2] = textColor;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const itemStyle = t1;
  const t2 = omit(props, "ref");
  let t3;
  if ($[4] !== children || $[5] !== itemStyle || $[6] !== t2) {
    t3 = <li {...t2} style={itemStyle}>{children}</li>;
    $[4] = children;
    $[5] = itemStyle;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const CodeBlockRenderer: Components['code'] = t0 => {
  const $ = _c(11);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    componentStyles
  } = useMarkdownContext();
  let t1;
  if ($[0] !== componentStyles?.code) {
    t1 = componentStyles?.code ?? {};
    $[0] = componentStyles?.code;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let codeStylesWithoutColor;
  if ($[2] !== t1) {
    const {
      color: _codeColor,
      ...t2
    } = t1;
    codeStylesWithoutColor = t2;
    $[2] = t1;
    $[3] = codeStylesWithoutColor;
  } else {
    codeStylesWithoutColor = $[3];
  }
  let t2;
  if ($[4] !== codeStylesWithoutColor || $[5] !== style) {
    t2 = {
      ...style,
      color: "initial",
      ...codeStylesWithoutColor
    };
    $[4] = codeStylesWithoutColor;
    $[5] = style;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const codeStyle = t2;
  const t3 = omit(props, "ref");
  let t4;
  if ($[7] !== children || $[8] !== codeStyle || $[9] !== t3) {
    t4 = <code {...t3} style={codeStyle}>{children}</code>;
    $[7] = children;
    $[8] = codeStyle;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const OrderedListRenderer: Components['ol'] = t0 => {
  const $ = _c(8);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    textColor,
    componentStyles
  } = useMarkdownContext();
  let t1;
  if ($[0] !== componentStyles || $[1] !== style || $[2] !== textColor) {
    t1 = applyComponentStyles("list", textColor, componentStyles, style);
    $[0] = componentStyles;
    $[1] = style;
    $[2] = textColor;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const listStyle = t1;
  const t2 = omit(props, "ref");
  let t3;
  if ($[4] !== children || $[5] !== listStyle || $[6] !== t2) {
    t3 = <ol {...t2} style={listStyle}>{children}</ol>;
    $[4] = children;
    $[5] = listStyle;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const BlockquoteRenderer: Components['blockquote'] = t0 => {
  const $ = _c(11);
  const {
    children,
    style,
    ...props
  } = t0;
  const {
    componentStyles
  } = useMarkdownContext();
  let t1;
  if ($[0] !== componentStyles?.blockquote) {
    t1 = componentStyles?.blockquote ?? {};
    $[0] = componentStyles?.blockquote;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let blockquoteStylesWithoutColor;
  if ($[2] !== t1) {
    const {
      color: _bqColor,
      ...t2
    } = t1;
    blockquoteStylesWithoutColor = t2;
    $[2] = t1;
    $[3] = blockquoteStylesWithoutColor;
  } else {
    blockquoteStylesWithoutColor = $[3];
  }
  let t2;
  if ($[4] !== blockquoteStylesWithoutColor || $[5] !== style) {
    t2 = {
      ...style,
      color: "initial",
      ...blockquoteStylesWithoutColor
    };
    $[4] = blockquoteStylesWithoutColor;
    $[5] = style;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const blockquoteStyle = t2;
  const t3 = omit(props, "ref");
  let t4;
  if ($[7] !== blockquoteStyle || $[8] !== children || $[9] !== t3) {
    t4 = <blockquote {...t3} style={blockquoteStyle}>{children}</blockquote>;
    $[7] = blockquoteStyle;
    $[8] = children;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const ImageRenderer: Components['img'] = t0 => {
  const $ = _c(8);
  const {
    src,
    alt,
    style,
    ...props
  } = t0;
  const {
    componentStyles
  } = useMarkdownContext();
  const t1 = componentStyles?.image;
  let t2;
  if ($[0] !== style || $[1] !== t1) {
    t2 = {
      ...style,
      ...t1
    };
    $[0] = style;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const imageStyle = t2;
  const t3 = omit(props, "ref");
  let t4;
  if ($[3] !== alt || $[4] !== imageStyle || $[5] !== src || $[6] !== t3) {
    t4 = <img src={src} alt={alt} style={imageStyle} {...t3} />;
    $[3] = alt;
    $[4] = imageStyle;
    $[5] = src;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};

/**
 * Component to use ReactMarkdown with preset styling and LinkRenderer
 * that opens the a tags in a new tab
 * @param source - markdown source to parse
 * @param className - add custom className on wrapper element
 * @param textColor - optional text color to apply to text elements (excluding code blocks, blockquotes, inline code, and links)
 * @param componentStyles - optional specific styles to apply to different component types
 */
export function MarkdownWrapper(t0) {
  const $ = _c(35);
  let className;
  let componentStyles;
  let components;
  let ref;
  let rest;
  let source;
  let style;
  let textColor;
  let useAnchorLink;
  if ($[0] !== t0) {
    ({
      source,
      className,
      style,
      components,
      textColor,
      componentStyles,
      useAnchorLink,
      ref,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = componentStyles;
    $[3] = components;
    $[4] = ref;
    $[5] = rest;
    $[6] = source;
    $[7] = style;
    $[8] = textColor;
    $[9] = useAnchorLink;
  } else {
    className = $[1];
    componentStyles = $[2];
    components = $[3];
    ref = $[4];
    rest = $[5];
    source = $[6];
    style = $[7];
    textColor = $[8];
    useAnchorLink = $[9];
  }
  let t1;
  if ($[10] !== useAnchorLink) {
    t1 = isBoolean(useAnchorLink) || false;
    $[10] = useAnchorLink;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== componentStyles || $[13] !== rest.currentHash || $[14] !== t1 || $[15] !== textColor) {
    t2 = {
      textColor,
      componentStyles,
      useAnchorLink: t1,
      currentHash: rest.currentHash
    };
    $[12] = componentStyles;
    $[13] = rest.currentHash;
    $[14] = t1;
    $[15] = textColor;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const contextValue = t2;
  let t3;
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [gfm];
    t4 = [rehypeRaw];
    $[17] = t3;
    $[18] = t4;
  } else {
    t3 = $[17];
    t4 = $[18];
  }
  let t5;
  if ($[19] !== className) {
    t5 = cx("app-markdown-wrapper", className);
    $[19] = className;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== components) {
    t6 = {
      ...components,
      link: LinkRenderer,
      a: ATagRenderer,
      p: Paragraph,
      ul: ListRenderer,
      ol: OrderedListRenderer,
      li: ListItemRenderer,
      code: CodeBlockRenderer,
      blockquote: BlockquoteRenderer,
      img: ImageRenderer,
      h1: Title3,
      h2: Title3,
      h3: Title3,
      h4: Title4,
      h5: Title5
    };
    $[21] = components;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== rest || $[24] !== source || $[25] !== t5 || $[26] !== t6) {
    t7 = <ReactMarkdown remarkPlugins={t3} rehypePlugins={t4} className={t5} components={t6} {...rest}>{source}</ReactMarkdown>;
    $[23] = rest;
    $[24] = source;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== contextValue || $[29] !== t7) {
    t8 = <MarkdownContext value={contextValue}>{t7}</MarkdownContext>;
    $[28] = contextValue;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== ref || $[32] !== style || $[33] !== t8) {
    t9 = <div ref={ref} style={style} data-testid="markdown-wrapper" tabIndex={0}>{t8}</div>;
    $[31] = ref;
    $[32] = style;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
}
export default MarkdownWrapper;